import { createGenericStore } from '@/stores/utils/genericStore.js'
import axios from 'axios'

const mergeBusinessSystemWithExistingUsers = (store, businessSystemId, incomingBusinessSystem) => {
  const existingBusinessSystem = store.getItemById(Number(businessSystemId))

  if (incomingBusinessSystem.users === undefined && existingBusinessSystem?.users !== undefined) {
    return { ...incomingBusinessSystem, users: existingBusinessSystem.users }
  }

  return incomingBusinessSystem
}

const businessSystemStoreOptions = {
  state: () => ({
    frequencyOptions: [
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'monthly', label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
      { value: 'annually', label: 'Annually' },
      { value: 'on_demand', label: 'On Demand' },
    ],
  }),
  getters: {
    getFrequencyOptions(state) {
      return state.frequencyOptions
    },
  },
  actions: {
    async createBusinessSystem(payload) {
      return this.itemCreate(payload)
    },
    async updateBusinessSystemBasics(businessSystemId, payload) {
      this.isLoading = true
      this.error = null

      try {
        const normalizedBusinessSystemId = Number(businessSystemId)
        const attributes = { ...payload }
        const desiredVisibility = attributes.public
        delete attributes.public

        let updatedBusinessSystem = this.getItemById(normalizedBusinessSystemId) ?? null

        if (Object.keys(attributes).length > 0) {
          const { data } = await axios.patch(`${this.api}/${normalizedBusinessSystemId}`, attributes)
          updatedBusinessSystem = mergeBusinessSystemWithExistingUsers(this, normalizedBusinessSystemId, data)
          this.itemPatchState(updatedBusinessSystem)
        }

        if (typeof desiredVisibility === 'boolean' && updatedBusinessSystem?.public !== desiredVisibility) {
          if (desiredVisibility) {
            updatedBusinessSystem = await this.setBusinessSystemAsPublic(normalizedBusinessSystemId)
          } else {
            updatedBusinessSystem = await this.setBusinessSystemAsPrivate(normalizedBusinessSystemId)
          }
        }

        return updatedBusinessSystem
      } catch (err) {
        this.error = err
        console.error(`Error updating business system ${businessSystemId}:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async addUsersToBusinessSystem(businessSystemId, payload) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.post(`${this.api}/${businessSystemId}/users`, payload)
        this.itemPatchState(data)

        return data
      } catch (err) {
        this.error = err
        console.error(`Error adding users for business system ${businessSystemId}:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async setBusinessSystemAsPublic(businessSystemId) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.patch(`${this.api}/${businessSystemId}/set-public`)
        const updatedBusinessSystem = mergeBusinessSystemWithExistingUsers(this, businessSystemId, data)

        this.itemPatchState(updatedBusinessSystem)

        return updatedBusinessSystem
      } catch (err) {
        this.error = err
        console.error(`Error setting business system ${businessSystemId} as public:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async setBusinessSystemAsPrivate(businessSystemId) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await axios.patch(`${this.api}/${businessSystemId}/set-private`)
        const updatedBusinessSystem = mergeBusinessSystemWithExistingUsers(this, businessSystemId, data)

        this.itemPatchState(updatedBusinessSystem)

        return updatedBusinessSystem
      } catch (err) {
        this.error = err
        console.error(`Error setting business system ${businessSystemId} as private:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async removeUserFromBusinessSystem(businessSystemId, userId) {
      this.isLoading = true
      this.error = null

      try {
        await axios.delete(`${this.api}/${businessSystemId}/users/${userId}`)

        return this.fetchItemById(businessSystemId)
      } catch (err) {
        this.error = err
        console.error(`Error removing user ${userId} from business system ${businessSystemId}:`, err)
        throw err
      } finally {
        this.isLoading = false
      }
    },
  }
}

export const useBusinessSystemStore = createGenericStore(
  'businessSystem',
  '/api/v1/business-systems',
  businessSystemStoreOptions
)
